import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { BookOpen, ClipboardList, Clock, GraduationCap, Library, PenLine, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../providers/LocaleProvider';
import { formatAppDate, formatAppDateTime } from '../../utils/localeDateFormat';
import type { AppLocale } from '../../i18n';
import { type LearningResumeEntry } from '../../utils/lastLearningPosition';
import { resolveKeepLearningEntryLabels } from '../../utils/keepLearningEntryLabels';
import { getIconByName } from '../../utils/iconUtils';
import { contentTagService, type PublicContentTagDto } from '../../services/contentTagService';
import { contentService, type ExerciseFlowItemDto, type QuizFlowItemDto } from '../../services/contentService';
import { useHistoryBackClose } from '../../hooks/useHistoryBackClose';
import { LEARNER_SIDE_BACKDROP_MS } from '../../utils/learnerUiAnim';
import { useModalBackdropFade } from '../../utils/learnerSlideModalMotion';

export interface KeepLearningChoiceModalProps {
  open: boolean;
  entries: LearningResumeEntry[];
  onClose: () => void;
  onSelect: (entry: LearningResumeEntry, anchorEl?: HTMLElement | null) => void;
  /** Called after exit animation completes — use to clear persisted list data. */
  onAfterClose?: () => void;
}

export interface KeepLearningChoiceModalHandle {
  resetSelection: () => void;
}

function resolveTagIconName(
  entry: LearningResumeEntry,
  publicTagsById: Map<number, PublicContentTagDto>
): string | undefined {
  if (entry.payload.kind !== 'tag') return undefined;
  const fromApi = publicTagsById.get(entry.payload.tagId);
  return fromApi?.icon ?? entry.icon;
}

function EntryRowIcon({ entry, tagIconName }: { entry: LearningResumeEntry; tagIconName?: string }) {
  if (entry.kind === 'tag') {
    const TagIcon = tagIconName ? getIconByName(tagIconName) : BookOpen;
    return <TagIcon className="h-5 w-5 text-gray-700 dark:text-gray-200" aria-hidden />;
  }

  switch (entry.kind) {
    case 'lesson':
      return <BookOpen className="h-5 w-5 text-gray-700 dark:text-gray-200" aria-hidden />;
    case 'quiz':
      return <ClipboardList className="h-5 w-5 text-gray-700 dark:text-gray-200" aria-hidden />;
    case 'exercise':
      return <PenLine className="h-5 w-5 text-gray-700 dark:text-gray-200" aria-hidden />;
    case 'courses':
      return <GraduationCap className="h-5 w-5 text-gray-700 dark:text-gray-200" aria-hidden />;
    case 'book':
      return <Library className="h-5 w-5 text-gray-700 dark:text-gray-200" aria-hidden />;
    default:
      return <BookOpen className="h-5 w-5 text-gray-700 dark:text-gray-200" aria-hidden />;
  }
}

function accentRing(entry: LearningResumeEntry): string {
  switch (entry.kind) {
    case 'lesson':
      return 'border-blue-200/80 bg-blue-50/90 hover:border-blue-300 dark:border-blue-800/50 dark:bg-blue-950/40 dark:hover:border-blue-700';
    case 'tag':
      return 'border-violet-200/80 bg-violet-50/90 hover:border-violet-300 dark:border-violet-800/50 dark:bg-violet-950/40 dark:hover:border-violet-700';
    case 'quiz':
      return 'border-rose-200/80 bg-rose-50/90 hover:border-rose-300 dark:border-rose-800/50 dark:bg-rose-950/40 dark:hover:border-rose-700';
    case 'exercise':
      return 'border-amber-200/80 bg-amber-50/90 hover:border-amber-300 dark:border-amber-800/50 dark:bg-amber-950/40 dark:hover:border-amber-700';
    case 'courses':
      return 'border-sky-200/80 bg-sky-50/90 hover:border-sky-300 dark:border-sky-800/50 dark:bg-sky-950/40 dark:hover:border-sky-700';
    case 'book':
      return 'border-emerald-200/80 bg-emerald-50/90 hover:border-emerald-300 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:hover:border-emerald-700';
    default:
      return 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900';
  }
}

function formatLastSeen(
  updatedAt: number,
  t: (key: string, opts?: Record<string, string>) => string,
  locale: AppLocale
): string {
  if (!updatedAt || !Number.isFinite(updatedAt)) return t('lastSeenUnknown');

  const date = new Date(updatedAt);
  if (Number.isNaN(date.getTime())) return t('lastSeenUnknown');

  const now = new Date();
  const time = formatAppDate(date, locale, {
    hour: 'numeric',
    minute: '2-digit',
    usePersianCalendar: false,
  });

  if (date.toDateString() === now.toDateString()) {
    return t('lastSeenToday', { time });
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return t('lastSeenYesterday', { time });
  }

  return formatAppDateTime(date, locale);
}

export const KeepLearningChoiceModal = forwardRef<KeepLearningChoiceModalHandle, KeepLearningChoiceModalProps>(function KeepLearningChoiceModal({
  open,
  entries,
  onClose,
  onSelect,
  onAfterClose
}, ref) {
  const { t } = useTranslation('keepLearning');
  const { t: tLearning } = useTranslation('learning');
  const { direction, locale } = useAppLocale();
  const isRtl = direction === 'rtl';
  const exitHandledRef = useRef(false);
  const selectingRef = useRef(false);
  const closeTimerRef = useRef<number | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [shouldRender, setShouldRender] = useState(open);
  const [publicTagsById, setPublicTagsById] = useState<Map<number, PublicContentTagDto>>(new Map());
  const [quizItemsByKey, setQuizItemsByKey] = useState<Map<string, QuizFlowItemDto>>(new Map());
  const [exerciseItemsByKey, setExerciseItemsByKey] = useState<Map<string, ExerciseFlowItemDto>>(new Map());

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    contentTagService
      .getPublicTags()
      .then((tags) => {
        if (!cancelled) setPublicTagsById(new Map(tags.map((t) => [t.id, t])));
      })
      .catch(() => {
        if (!cancelled) setPublicTagsById(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const needsQuiz = entries.some((e) => e.kind === 'quiz');
    const needsExercise = entries.some((e) => e.kind === 'exercise');
    if (!needsQuiz && !needsExercise) {
      setQuizItemsByKey(new Map());
      setExerciseItemsByKey(new Map());
      return undefined;
    }

    let cancelled = false;
    void (async () => {
      try {
        const [quizItems, exerciseItems] = await Promise.all([
          needsQuiz ? contentService.getQuizFlowItems() : Promise.resolve([]),
          needsExercise ? contentService.getExerciseFlowItems() : Promise.resolve([])
        ]);
        if (cancelled) return;
        setQuizItemsByKey(new Map(quizItems.map((item) => [`${item.kind}-${item.quizId}`, item])));
        setExerciseItemsByKey(
          new Map(exerciseItems.map((item) => [`${item.kind}-${item.exerciseId}`, item]))
        );
      } catch {
        if (!cancelled) {
          setQuizItemsByKey(new Map());
          setExerciseItemsByKey(new Map());
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, entries]);

  const labelContext = useMemo(
    () => ({
      locale,
      tLearning,
      publicTagsById,
      quizItemsByKey,
      exerciseItemsByKey
    }),
    [locale, tLearning, publicTagsById, quizItemsByKey, exerciseItemsByKey]
  );

  const tagIconByEntryId = useMemo(() => {
    const map = new Map<string, string | undefined>();
    for (const entry of entries) {
      if (entry.kind !== 'tag') continue;
      map.set(entry.id, resolveTagIconName(entry, publicTagsById));
    }
    return map;
  }, [entries, publicTagsById]);

  const finishUnmount = useCallback(() => {
    if (exitHandledRef.current || open) return;
    exitHandledRef.current = true;
    setShouldRender(false);
    onAfterClose?.();
  }, [open, onAfterClose]);

  useEffect(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    if (open) {
      exitHandledRef.current = false;
      selectingRef.current = false;
      setIsSelecting(false);
      setShouldRender(true);
      return;
    }

    closeTimerRef.current = window.setTimeout(finishUnmount, LEARNER_SIDE_BACKDROP_MS + 40);
  }, [open, finishUnmount]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useHistoryBackClose(open, handleClose, 'keep-learning');

  const resetSelection = useCallback(() => {
    selectingRef.current = false;
    setIsSelecting(false);
  }, []);

  useImperativeHandle(ref, () => ({ resetSelection }), [resetSelection]);

  const handleSelect = useCallback(
    (entry: LearningResumeEntry, anchorEl?: HTMLElement | null) => {
      if (selectingRef.current) return;
      selectingRef.current = true;
      setIsSelecting(true);
      onSelect(entry, anchorEl ?? null);
    },
    [onSelect]
  );

  const backdropVisible = useModalBackdropFade(shouldRender, !open);

  if (!shouldRender) return null;

  return (
    <div className="learner-slide-modal-root fixed inset-0 z-[200] py-6 sm:py-10" role="presentation">
      <div
        className={`keyboard-slide-modal-backdrop fixed inset-0 bg-black/45 backdrop-blur-[2px] pointer-events-auto dark:bg-black/55${backdropVisible ? ' is-visible' : ''}`}
        onClick={handleClose}
        aria-hidden="true"
      />
      <div className="flex min-h-full items-end justify-center px-3 sm:items-center sm:px-6">
      <div
        dir={direction}
        className={`keyboard-slide-modal-panel keyboard-slide-modal-panel--from-banner mme-tone pointer-events-auto relative flex max-h-[min(70vh,32rem)] w-full max-w-md flex-col overflow-hidden rounded-t-2xl border border-gray-200/90 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900 sm:rounded-2xl${backdropVisible ? ' is-visible' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="keep-learning-choice-title"
      >
          <div className="relative border-b border-gray-100 bg-gradient-to-br from-indigo-50 via-white to-sky-50 px-5 py-4 dark:border-gray-800 dark:from-indigo-950/40 dark:via-gray-900 dark:to-sky-950/30">
            <button
              type="button"
              onClick={handleClose}
              className={`absolute top-3 rounded-xl p-2 text-gray-500 transition-colors hover:bg-white/80 hover:text-gray-800 dark:hover:bg-gray-800 dark:hover:text-gray-200 ${
                isRtl ? 'left-3' : 'right-3'
              }`}
              aria-label={t('closeAriaLabel')}
            >
              <X className="h-5 w-5" />
            </button>
            <h2
              id="keep-learning-choice-title"
              className={`text-start text-lg font-bold text-gray-900 dark:text-gray-100 ${isRtl ? 'pl-10' : 'pr-10'}`}
            >
              {t('title')}
            </h2>
            <p className="mt-1 text-start text-sm text-gray-600 dark:text-gray-400">{t('subtitle')}</p>
          </div>

          <ul className="max-h-[min(60vh,22rem)] space-y-2 overflow-y-auto p-4">
            {entries.map((entry) => {
              const labels = resolveKeepLearningEntryLabels(entry, labelContext);
              const lastSeenLabel = formatLastSeen(entry.updatedAt, t, locale);
              const lastSeenIso = Number.isFinite(entry.updatedAt)
                ? new Date(entry.updatedAt).toISOString()
                : undefined;
              const tagIconName = entry.kind === 'tag' ? tagIconByEntryId.get(entry.id) : undefined;
              const lessonPathParts = labels.lessonPathParts;
              const showLessonMap = entry.kind === 'lesson' && Boolean(lessonPathParts?.length);

              return (
                <li key={entry.id}>
                  <button
                    type="button"
                    onClick={(e) => handleSelect(entry, e.currentTarget)}
                    disabled={isSelecting}
                    className={`flex w-full items-start gap-3 rounded-xl border p-3 text-start shadow-sm transition-all hover:shadow-md disabled:pointer-events-none disabled:opacity-60 ${accentRing(entry)}`}
                  >
                    <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/80 shadow-sm dark:bg-gray-950/50">
                      <EntryRowIcon entry={entry} tagIconName={tagIconName} />
                    </span>
                    <span className="min-w-0 flex-1 text-start">
                      <span className="block font-semibold text-gray-900 dark:text-gray-100">{labels.title}</span>
                      {showLessonMap ? (
                        <span className="mt-1 flex flex-wrap items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                          {(lessonPathParts ?? []).map((part, index, parts) => (
                            <React.Fragment key={`${part}-${index}`}>
                              {index > 0 ? (
                                <span className="text-gray-400 dark:text-gray-500" aria-hidden>
                                  ›
                                </span>
                              ) : null}
                              <span className={index === parts.length - 1 ? 'font-medium text-gray-700 dark:text-gray-300' : ''}>
                                {part}
                              </span>
                            </React.Fragment>
                          ))}
                        </span>
                      ) : labels.subtitle ? (
                        <span className="mt-0.5 block truncate text-sm text-gray-600 dark:text-gray-400">
                          {labels.subtitle}
                        </span>
                      ) : null}
                      <span className="mt-1.5 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                        <Clock className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                        <time dateTime={lastSeenIso}>{t('lastSeen', { when: lastSeenLabel })}</time>
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
      </div>
      </div>
    </div>
  );
});
